import React, { useEffect, useRef, useState } from "react";
import classes from "./GamePage.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import { SAVE_PLAYTIME_API } from "../http/http";
import { useSelector } from "react-redux";

const GamePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);

  const iframeRef = useRef(null);
  const gameRef = useRef(null);

  const hasIframeFocusRef = useRef(false);
  const isRunningRef = useRef(false);
  const startedAtRef = useRef(null);
  const accumulatedMsRef = useRef(0);
  const sessionSavedRef = useRef(false);
  const sessionStartedAtRef = useRef(null);

  const { user } = useSelector((state) => state?.persisted?.userSlice);

  useEffect(() => {
    if (user?.token) {
      if (!location?.state?.game) {
        navigate("/home");
      } else {
        setGame(location.state.game);
      }
    } else {
      navigate("/subscribe");
    }
  }, [location, navigate]);

  useEffect(() => {
    gameRef.current = game;

    // reset tracking whenever a new game comes
    hasIframeFocusRef.current = false;
    isRunningRef.current = false;
    startedAtRef.current = null;
    accumulatedMsRef.current = 0;
    sessionSavedRef.current = false;
    sessionStartedAtRef.current = null;
  }, [game]);

  const shouldRunTimer = () => {
    return (
      !!gameRef.current &&
      hasIframeFocusRef.current &&
      document.visibilityState === "visible" &&
      document.hasFocus()
    );
  };

  const startTimer = () => {
    if (isRunningRef.current) return;
    if (!shouldRunTimer()) return;

    startedAtRef.current = Date.now();
    isRunningRef.current = true;

    if (!sessionStartedAtRef.current) {
      sessionStartedAtRef.current = new Date().toISOString();
    }
  };

  const stopTimer = () => {
    if (!isRunningRef.current) return;

    accumulatedMsRef.current += Date.now() - startedAtRef.current;
    startedAtRef.current = null;
    isRunningRef.current = false;
  };

  const getTotalPlayedMs = () => {
    let total = accumulatedMsRef.current;

    if (isRunningRef.current && startedAtRef.current) {
      total += Date.now() - startedAtRef.current;
    }

    return total;
  };

  const reevaluateTimer = () => {
    if (shouldRunTimer()) {
      startTimer();
    } else {
      stopTimer();
    }
  };

  const sendPlayTime = async (payload, useBeacon = false) => {
    const body = JSON.stringify(payload);

    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      return navigator.sendBeacon(SAVE_PLAYTIME_API, blob);
    }

    console.log("enter here in play time exit");

    return fetch(SAVE_PLAYTIME_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      //   credentials: "include",
      keepalive: true,
      body,
    });
  };

  const flushPlayTime = async (reason = "manual_exit", useBeacon = false) => {
    if (!gameRef.current || sessionSavedRef.current) return;

    stopTimer();

    const totalPlayedMs = getTotalPlayedMs();

    // optional: ignore very tiny sessions
    if (totalPlayedMs < 1000) {
      sessionSavedRef.current = true;
      return;
    }

    const payload = {
      game_id: game?.id,
      game_name: game?.game_name,
      game_category: game?.category_name,
      game_banner: game?.game_banner,
      game_url: game?.game_url,
      total_time_spent_seconds: Math.floor(totalPlayedMs / 1000),
      started_at: sessionStartedAtRef.current,
      ended_at: new Date().toISOString(),
      token: user?.token,
      reason,
    };

    try {
      sessionSavedRef.current = true;
      await sendPlayTime(payload, useBeacon);
      console.log("Play time saved:", payload);
    } catch (error) {
      sessionSavedRef.current = false;
      console.error("Failed to save play time:", error);
    }
  };

  const handleIframeFocus = () => {
    hasIframeFocusRef.current = true;
    reevaluateTimer();
  };

  const handleIframeBlur = () => {
    hasIframeFocusRef.current = false;
    reevaluateTimer();
  };

  const handleClose = async () => {
    await flushPlayTime("close_button_exit", false);
    navigate(-1);
  };

  useEffect(() => {
    if (!game) return;

    const handleVisibilityChange = () => {
      reevaluateTimer();
    };

    const handleWindowFocus = () => {
      reevaluateTimer();
    };

    const handleWindowBlur = () => {
      reevaluateTimer();
    };

    const handlePageHide = () => {
      flushPlayTime("pagehide", true);
    };

    const handleBeforeUnload = () => {
      flushPlayTime("beforeunload", true);
    };

    // fallback check because iframe focus/blur can be inconsistent in some browsers
    const focusWatcher = setInterval(() => {
      const iframeFocused = document.activeElement === iframeRef.current;

      if (iframeFocused !== hasIframeFocusRef.current) {
        hasIframeFocusRef.current = iframeFocused;
        reevaluateTimer();
      }
    }, 500);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(focusWatcher);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      flushPlayTime("component_unmount", false);
    };
  }, [game]);

  if (!game) return null;

  return (
    <main className={classes.main}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={classes.pageOverlay}
      >
        <div className={classes.wrapper}>
          <div className={classes.header}>
            <div className={classes.brandWrap}>
              <div className={classes.titleWrap}>
                <h3 className={classes.gameTitle}>
                  {game?.game_name || game?.name}
                </h3>
                <p className={classes.gameCategory}>
                  {game?.category_name || game?.categoryName}
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className={classes.closeBtn}
              type="button"
            >
              <FaTimes />
            </button>
          </div>

          <div className={classes.iframeShell}>
            <iframe
              ref={iframeRef}
              src={game?.game_url || game?.url}
              className={classes.iframe}
              title={game?.game_name || game?.name}
              allow="autoplay; fullscreen; clipboard-read; clipboard-write"
              onFocus={handleIframeFocus}
              onBlur={handleIframeBlur}
            />
          </div>
        </div>
      </motion.div>
    </main>
  );
};

export default GamePage;
